import { IReviewService } from '@/domain/services/review.service.interface';
import {
  ReviewEntity,
  ReviewsListResult,
  ReviewStats,
  ReviewFilters,
  CreateReviewData,
} from '@/domain/entities/review.entity';
import type { Review } from '@template-project/storefront-bff-client';
import { createBffClient } from '@/lib/bff/create-storefront-client';

function mapReview(r: Review): ReviewEntity {
  return {
    id: r.id,
    productId: r.productId,
    customerId: r.customerId,
    customerName: r.customerName,
    rating: r.rating,
    title: r.title,
    content: r.content,
    verified: r.verified,
    helpful: r.helpful,
    notHelpful: r.notHelpful,
    customerImage: r.customerImage,
    productVariant: r.productVariant,
    createdAt: new Date(r.createdAt),
    updatedAt: new Date(r.updatedAt),
  };
}

export class ReviewBffService implements IReviewService {
  private client = createBffClient({ includeStoreScope: true });

  async getReviewsByProductId(
    productId: string,
    options?: {
      limit?: number;
      offset?: number;
      filters?: ReviewFilters;
      includeStats?: boolean;
    },
  ): Promise<ReviewsListResult> {
    const res = await this.client.listReviews(productId, {
      limit: options?.limit,
      offset: options?.offset,
      rating: options?.filters?.rating,
      minRating: options?.filters?.minRating,
      maxRating: options?.filters?.maxRating,
      verified: options?.filters?.verified,
      includeStats: options?.includeStats,
    });

    return {
      reviews: res.data.map(mapReview),
      total: res.meta.total,
      limit: res.meta.limit,
      offset: res.meta.offset,
      hasMore: res.meta.hasMore,
      stats: res.meta.stats
        ? {
            averageRating: res.meta.stats.averageRating ?? 0,
            totalReviews: res.meta.stats.totalReviews,
            ratingDistribution: res.meta.stats.ratingDistribution as Record<number, number>,
          }
        : undefined,
    };
  }

  async getReviewStats(productId: string): Promise<ReviewStats> {
    const res = await this.client.getReviewStats(productId);
    return {
      averageRating: res.data.averageRating ?? 0,
      totalReviews: res.data.totalReviews,
      ratingDistribution: res.data.ratingDistribution as Record<number, number>,
    };
  }

  async voteReview(
    reviewId: string,
    voteType: 'helpful' | 'not_helpful',
  ): Promise<ReviewEntity> {
    const res = await this.client.voteReview(reviewId, voteType);
    return mapReview(res.data);
  }

  async createReview(reviewData: CreateReviewData): Promise<ReviewEntity> {
    const res = await this.client.createReview({
      productId: reviewData.productId,
      customerId: reviewData.customerId,
      rating: reviewData.rating,
      title: reviewData.title,
      content: reviewData.content,
      verified: reviewData.verified,
    });
    return mapReview(res.data);
  }
}
